import { useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { Badge } from '../../components/ui/Badge.js';
import { Spinner } from '../../components/ui/Spinner.js';
import type { InternalUser } from '@ember/types';

interface Stats {
  totalUsers: number;
  activeRooms: number;
  allowedDomains: number;
  unusedInvites: number;
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      // GET /api/admin/internal-users → { internalUsers: InternalUser[] }
      api.get<{ internalUsers: InternalUser[] }>('/api/admin/internal-users'),
      // GET /api/admin/domains → { domains: AllowedDomain[] }
      api.get<{ domains: { id: string }[] }>('/api/admin/domains'),
      // GET /api/admin/invites → { invites: Invite[] }
      api.get<{ invites: Array<{ usedAt: string | null }> }>('/api/admin/invites'),
      // GET /api/admin/users → { users: User[], total: number }
      api.get<{ total: number }>('/api/admin/users'),
    ])
      .then(([internalUsers, domains, invites, usersResp]) => {
        setStats({
          totalUsers: usersResp.total,
          activeRooms: 0, // future endpoint
          allowedDomains: domains.domains.length,
          unusedInvites: invites.invites.filter((i) => !i.usedAt).length,
        });
      })
      .catch(() => {
        // best-effort — show partial data
        setStats({ totalUsers: 0, activeRooms: 0, allowedDomains: 0, unusedInvites: 0 });
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3">
        <Spinner />
        <span className="text-neutral-400 text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-neutral-100 mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats?.totalUsers ?? 0, icon: '👥' },
          { label: 'Allowed Domains', value: stats?.allowedDomains ?? 0, icon: '🌐' },
          { label: 'Unused Invites', value: stats?.unusedInvites ?? 0, icon: '✉️' },
          { label: 'Active Rooms', value: stats?.activeRooms ?? 0, icon: '🔴' },
        ].map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-neutral-800 bg-neutral-900 p-5"
          >
            <div className="text-2xl mb-2">{card.icon}</div>
            <div className="text-3xl font-bold text-neutral-100">{card.value}</div>
            <div className="text-sm text-neutral-500 mt-1">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-neutral-800 bg-neutral-900 p-5">
        <h2 className="text-base font-semibold text-neutral-100 mb-3">System Status</h2>
        <div className="flex flex-col gap-2 text-sm">
          {[
            'Cloudflare D1',
            'Durable Objects',
            'Calls / RealtimeKit',
            'Cloudflare Access',
          ].map((svc) => (
            <div key={svc} className="flex justify-between">
              <span className="text-neutral-400">{svc}</span>
              <Badge variant="success">Operational</Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
